import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useStore } from 'react-redux';
import { useAppSelector, useAppDispatch } from './redux';
import type { RootState } from '../store/store';
import { ServerMessagingProvider } from '../services/messaging/providers/ServerMessagingProvider';
import { IMessage } from '../services/messaging/IMessagingProvider';
import { NotificationService } from '../utils/notifications';
import { encryptMessage, getKeyPair } from '../services/encryption';
import {
  setConnected,
  setConversations,
  setMessages,
  addMessage,
  setUserOnline,
  setUserOffline,
  markConversationAsRead,
  markMessagesReadByRecipient,
  setUserTyping,
  setUserLastSeen,
  setMessageReaction,
  MESSAGE_CACHE_TTL
} from '../store/reducers/messages.reducer';

const API_BASE = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080');

/** The user is actually looking at the chat with `userId` (app visible, on the messages page, that chat open). */
const isViewingChat = (userId: string, openConversation: string | null) =>
  openConversation === userId &&
  document.visibilityState === 'visible' &&
  window.location.pathname.startsWith('/dashboard/messages');

/**
 * Mark the given received messages read on the server, tell the sender (live ✓✓),
 * and update local state. Local state only changes if the server accepted it, so
 * a failed request is retried next time the chat is opened.
 */
async function markIdsRead(
  provider: ServerMessagingProvider,
  dispatch: ReturnType<typeof useAppDispatch>,
  otherUserId: string,
  messageIds: string[]
) {
  if (messageIds.length === 0) return;
  if (!await provider.markManyAsRead(messageIds)) return;
  provider.sendReadReceipt(otherUserId, messageIds);
  dispatch(markConversationAsRead(otherUserId));
}

export const useMessaging = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { loginUser } = useAppSelector((state) => state.user);
  const { conversations, currentConversation, messages, isConnected, lastFetchedAt, typingUsers, lastSeen } = useAppSelector((state) => state.messages);
  const [provider, setProvider] = useState<ServerMessagingProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const providerRef = useRef<ServerMessagingProvider | null>(null);
  const currentConversationRef = useRef<string | null>(currentConversation);
  currentConversationRef.current = currentConversation;

  // Initialize messaging provider
  useEffect(() => {
    if (!loginUser?.id || providerRef.current) return;

    const initProvider = async () => {
      try {
        const senderName = loginUser.display_name || loginUser.email || 'Someone';
        const newProvider = new ServerMessagingProvider(String(loginUser.id), senderName);
        providerRef.current = newProvider;

        await newProvider.connect();
        setProvider(newProvider);
        dispatch(setConnected(true));

        // Request notification permission
        await NotificationService.requestPermission();

        // Load conversations
        const convs = await newProvider.getAllConversations();
        dispatch(setConversations(convs));

        // Listen for new messages
        newProvider.onMessageReceived((message: IMessage) => {
          dispatch(addMessage({ ...message, currentUserId: String(loginUser.id) }));
          // Arrived while the chat is open on screen — it's been read.
          const fromId = String(message.senderId);
          if (fromId !== String(loginUser.id) && isViewingChat(fromId, currentConversationRef.current)) {
            markIdsRead(newProvider, dispatch, fromId, [message.id]);
          }
        });

        // Read receipts — the other user read messages we sent them
        newProvider.onReadReceipt((byUserId: string, messageIds: string[]) => {
          dispatch(markMessagesReadByRecipient({ conversationUserId: byUserId, messageIds }));
        });

        // Listen for online status changes
        newProvider.onUserOnlineStatusChanged((userId: string, isOnline: boolean) => {
          if (isOnline) {
            dispatch(setUserOnline(userId));
            dispatch(setUserLastSeen({ userId, timestamp: null }));
          } else {
            dispatch(setUserOffline(userId));
            // Fetch the exact last-seen timestamp the server just recorded;
            // fall back to "now" if the request fails.
            newProvider.getLastSeen(userId)
              .then(ts => dispatch(setUserLastSeen({ userId, timestamp: ts ?? Date.now() })))
              .catch(() => dispatch(setUserLastSeen({ userId, timestamp: Date.now() })));
          }
        });

        // Typing indicators from the other participant
        newProvider.onTypingChanged((userId: string, isTyping: boolean) => {
          dispatch(setUserTyping({ userId, isTyping }));
        });

        // Reaction add/remove from the other participant
        newProvider.onReactionChanged((messageId: string, userId: string, emoji: string, action: 'add' | 'remove') => {
          dispatch(setMessageReaction({ conversationUserId: userId, messageId, userId, emoji, remove: action === 'remove' }));
        });

        // After a long backgrounding + reconnect, re-fetch the open conversation
        // so any messages missed while the socket was effectively dead show up
        // without the user having to close and reopen the chat.
        newProvider.onBackfillNeeded(() => {
          const userId = currentConversationRef.current;
          if (!userId) return;
          newProvider.getMessages(userId).then(msgs => {
            dispatch(setMessages({ userId, messages: msgs }));
            // Back on screen with this chat open — mark what arrived while away
            if (isViewingChat(userId, currentConversationRef.current)) {
              const unread = msgs.filter(m => m.senderId === userId && m.status !== 'read').map(m => m.id);
              markIdsRead(newProvider, dispatch, userId, unread);
            }
          }).catch(() => {});
        });
      } catch (err: any) {
        console.error('Failed to initialize messaging:', err);
        setError(err.message);
        dispatch(setConnected(false));
      }
    };

    initProvider();

    return () => {
      if (providerRef.current) {
        providerRef.current.disconnect();
        providerRef.current = null;
      }
    };
  }, [loginUser, dispatch]);

  const sendMessage = useCallback(async (recipientId: string, content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string; linkPreview?: any }) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');
    if (!loginUser?.id) throw new Error('User not logged in');

    try {
      const message = await providerRef.current.sendMessage(recipientId, content, mediaData);
      dispatch(addMessage({ ...message, currentUserId: String(loginUser.id) }));
      return message;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch, loginUser]);

  const loadMessages = useCallback(async (userId: string, forceRefresh = false) => {
    if (!providerRef.current) return;

    // Skip fetch if we have fresh cached messages — socket keeps them live anyway
    if (!forceRefresh) {
      const fetchedAt = lastFetchedAt[userId];
      const hasCached = messages[userId]?.length > 0;
      if (hasCached && fetchedAt && Date.now() - fetchedAt < MESSAGE_CACHE_TTL) {
        return; // cache is fresh — use Redux data, no network call
      }
    }

    try {
      const msgs = await providerRef.current.getMessages(userId);
      dispatch(setMessages({ userId, messages: msgs, updateFetchTime: true }));
    } catch (err: any) {
      setError(err.message);
    }
  }, [dispatch, lastFetchedAt, messages]);

  const markAsRead = useCallback(async (userId: string) => {
    if (!providerRef.current) return;

    // Read from the store at call time, not the render closure — callers run this
    // right after loadMessages(), and the closure still holds the pre-load list
    // (empty on a first open), so nothing was getting marked.
    const unreadIds = (store.getState().messages.messages[userId] || [])
      .filter(msg => msg.recipientId === String(loginUser?.id) && msg.status !== 'read')
      .map(msg => msg.id);

    if (unreadIds.length > 0) {
      await markIdsRead(providerRef.current, dispatch, userId, unreadIds); // one batched request
    } else {
      dispatch(markConversationAsRead(userId)); // still clear the unread badge
    }
  }, [store, loginUser, dispatch]);

  const isUserOnline = useCallback(async (userId: string): Promise<boolean> => {
    if (!providerRef.current) return false;
    return await providerRef.current.isOnline(userId);
  }, []);

  const deleteConversation = useCallback(async (userId: string) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');

    try {
      await providerRef.current.deleteConversation(userId);
      // Reload conversations after deletion
      const convs = await providerRef.current.getAllConversations();
      dispatch(setConversations(convs));
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch]);

  // Periodically reload current conversation so expired messages disappear
  useEffect(() => {
    if (!currentConversation || !providerRef.current) return;
    const interval = setInterval(async () => {
      const provider = providerRef.current;
      if (!provider) return;
      const msgs = await provider.getMessages(currentConversation);
      dispatch(setMessages({ userId: currentConversation, messages: msgs }));
      // Catch anything that slipped past the live connection while it was flaky
      if (isViewingChat(currentConversation, currentConversationRef.current)) {
        const unread = msgs.filter(m => m.senderId === currentConversation && m.status !== 'read').map(m => m.id);
        markIdsRead(provider, dispatch, currentConversation, unread);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [currentConversation, dispatch]);

  // Get current conversation messages, filtering out any locally-cached expired ones
  const currentMessages = useMemo(() => {
    if (!currentConversation) return [];
    const now = Date.now();
    return (messages[currentConversation] || []).filter(
      m => !m.expiresAt || m.expiresAt > now
    );
  }, [currentConversation, messages]);

  // Edit a message — re-encrypts with recipient's current public key.
  // Refuses to send if encryption is not possible (no plaintext ever leaves the device).
  const editMessage = useCallback(async (messageId: string, recipientId: string, newText: string): Promise<void> => {
    const keyPair = getKeyPair();
    if (!keyPair) throw new Error('No encryption keys');

    const recipientKey = await providerRef.current?.getUserPublicKey(recipientId);
    if (!recipientKey) throw new Error('Cannot edit: recipient public key unavailable');
    const content = encryptMessage(newText, recipientKey, keyPair.secretKey);

    await axios.put(`${API_BASE}/api/messages/${messageId}`, { content, isEncrypted: true }, { withCredentials: true });

    // Emit plaintext to recipient via socket (TLS-secured, user-specific room)
    // so their handler can store it directly without decrypting
    const socket = providerRef.current?.getSocket();
    const editedAt = Date.now();
    socket?.emit('message-edited', { recipientId, messageId, newContent: newText, editedAt });

    // Update local Redux state
    if (currentConversation) {
      dispatch(setMessages({
        userId: currentConversation,
        messages: (messages[currentConversation] || []).map(m =>
          m.id === messageId ? { ...m, content: newText, editedAt } : m
        )
      }));
    }
  }, [currentConversation, messages, dispatch]);

  // Delete a message — scope: 'self' (hide for me) | 'everyone' (hard delete)
  const deleteMessage = useCallback(async (messageId: string, recipientId: string, scope: 'self' | 'everyone'): Promise<void> => {
    await axios.delete(`${API_BASE}/api/messages/${messageId}?scope=${scope}`, { withCredentials: true });

    const socket = providerRef.current?.getSocket();
    if (scope === 'everyone') {
      socket?.emit('message-deleted', { recipientId, messageId, scope });
    }

    // Remove from local Redux state
    if (currentConversation) {
      dispatch(setMessages({
        userId: currentConversation,
        messages: (messages[currentConversation] || []).filter(m => m.id !== messageId)
      }));
    }
  }, [currentConversation, messages, dispatch]);

  // Notify the recipient that we're typing (or stopped) — fire-and-forget over the socket.
  const sendTyping = useCallback((recipientId: string, isTyping: boolean) => {
    providerRef.current?.sendTyping(recipientId, isTyping);
  }, []);

  // Add/replace our reaction on a message. Updates Redux immediately (optimistic).
  const setReaction = useCallback(async (messageId: string, recipientId: string, emoji: string): Promise<void> => {
    if (!providerRef.current || !loginUser?.id) return;
    dispatch(setMessageReaction({ conversationUserId: recipientId, messageId, userId: String(loginUser.id), emoji }));
    await providerRef.current.setReaction(messageId, recipientId, emoji);
  }, [dispatch, loginUser]);

  // Remove our reaction from a message. Updates Redux immediately (optimistic).
  const removeReaction = useCallback(async (messageId: string, recipientId: string): Promise<void> => {
    if (!providerRef.current || !loginUser?.id) return;
    dispatch(setMessageReaction({ conversationUserId: recipientId, messageId, userId: String(loginUser.id), emoji: '', remove: true }));
    await providerRef.current.removeReaction(messageId, recipientId);
  }, [dispatch, loginUser]);

  return {
    isConnected,
    conversations,
    currentConversation,
    messages: currentMessages,
    typingUsers,
    lastSeen,
    error,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMessages,
    markAsRead,
    isUserOnline,
    deleteConversation,
    sendTyping,
    setReaction,
    removeReaction,
    provider
  };
};
